import { loginRequest } from "config/auth-config";
import { msalInstance } from "config/msal-instance";
import { env } from "env";

export interface StreamFetchOptions {
  body?: unknown;
  method?: string;
  headers?: Record<string, string>;
  onChunk?: (parsed: unknown, fullContent: string) => void;
  onError?: (error: Error) => void;
  onComplete?: (fullContent: string, chunks: unknown[]) => void;
  parseJson?: boolean;
  signal?: AbortSignal;
}

export interface StreamFetchResult {
  content: string;
  chunks: unknown[];
  aborted?: boolean;
}

const getBaseURL = (): string => {
  const baseUrl = env?.REACT_APP_HARBOR_API_BASE_URL;
  if (!baseUrl) {
    throw new Error("REACT_APP_HARBOR_API_BASE_URL is not defined in environment variables");
  }
  return baseUrl;
};

/**
 * Universal SSE streaming fetch.
 * Uses fetch with Accept: text/event-stream, reads the response body as a stream,
 * parses SSE events (optionally as JSON), and invokes onChunk for each event.
 * Per SSE spec: multiple consecutive "data:" lines form one event (concatenated with newline).
 */
export const streamFetch = async (
  url: string,
  options: StreamFetchOptions = {},
): Promise<StreamFetchResult> => {
  const {
    body,
    method = "POST",
    headers: additionalHeaders = {},
    onChunk,
    onError,
    onComplete,
    parseJson = true,
    signal,
  } = options;

  let authHeader = "";
  try {
    await msalInstance.initialize();
    const response = await msalInstance.acquireTokenSilent(loginRequest);
    authHeader = `Bearer ${response.accessToken}`;
  } catch (err) {
    onError?.(err instanceof Error ? err : new Error(String(err)));
    throw err;
  }

  const defaultHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
    Authorization: authHeader,
  };

  const fullUrl = url.startsWith("http") ? url : `${getBaseURL()}${url}`;
  const response = await fetch(fullUrl, {
    method,
    headers: { ...defaultHeaders, ...additionalHeaders },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`);
    onError?.(error);
    throw error;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    const error = new Error("Response body is not readable");
    onError?.(error);
    throw error;
  }

  const decoder = new TextDecoder();
  let fullContent = "";
  const chunks: unknown[] = [];
  let buffer = "";
  /** Per SSE spec: multiple "data:" lines form one event; we accumulate their payloads. */
  let dataLinePayloads: string[] = [];

  const flushEvent = (payload: string) => {
    if (!payload || payload === "[DONE]") return;
    let parsed: unknown = payload;
    if (parseJson && payload) {
      try {
        parsed = JSON.parse(payload);
      } catch {
        // Keep as string if not valid JSON
      }
    }
    const content =
      typeof parsed === "string" ? parsed : ((parsed as { content?: string })?.content ?? "");
    fullContent += content;
    chunks.push(parsed);
    onChunk?.(parsed, fullContent);
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed === "") {
          if (dataLinePayloads.length > 0) {
            // SSE spec: multiple data lines are concatenated with newline; when backend splits one JSON across lines, join with "" to reassemble
            const joined = dataLinePayloads.join("");
            flushEvent(joined);
            dataLinePayloads = [];
          }
          continue;
        }
        if (trimmed.startsWith("data:")) {
          const data = trimmed.slice(5).trim();
          if (data === "[DONE]") break;
          dataLinePayloads.push(data);
        } else {
          if (dataLinePayloads.length > 0) {
            const joined = dataLinePayloads.join("");
            flushEvent(joined);
            dataLinePayloads = [];
          }
        }
      }
    }

    if (dataLinePayloads.length > 0) {
      const joined = dataLinePayloads.join("");
      flushEvent(joined);
      dataLinePayloads = [];
    }

    // Process any remaining buffer (incomplete line not yet terminated by \n)
    if (buffer.trim()) {
      const trimmed = buffer.trim();
      if (trimmed.startsWith("data:")) {
        const data = trimmed.slice(5).trim();
        if (data && data !== "[DONE]") {
          flushEvent(data);
        }
      }
    }
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      onError?.(new Error("Stream aborted"));
      return { content: fullContent, chunks, aborted: true };
    }
    onError?.(error instanceof Error ? error : new Error(String(error)));
    throw error;
  } finally {
    reader.releaseLock();
  }

  onComplete?.(fullContent, chunks);
  return { content: fullContent, chunks };
};
